'use client';

import React, { forwardRef } from 'react';
import { cn } from '@/lib/cn';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    label?: string;
    error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
    ({ label, error, className = '', id, required, ...props }, ref) => {
        const textareaId = id || label?.toLowerCase().replace(/\s+/g, '-');
        return (
            <div className="space-y-1.5">
                {label && (
                    <label htmlFor={textareaId} className="block text-xs text-zinc-400 font-medium">
                        {label}{required && <span className="text-rose-400 ml-0.5">*</span>}
                    </label>
                )}
                <textarea
                    ref={ref}
                    id={textareaId}
                    required={required}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? `${textareaId}-error` : undefined}
                    className={cn(
                        'w-full bg-bg-surface border rounded-xl px-4 py-3',
                        'text-sm text-white placeholder-zinc-600',
                        'transition-colors duration-150',
                        'focus:outline-none focus:border-border-strong focus:ring-1 focus:ring-zinc-500/30',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        'resize-y min-h-[80px]',
                        error ? 'border-rose-500/50' : 'border-border-default',
                        className,
                    )}
                    {...props}
                />
                {error && (
                    <p id={`${textareaId}-error`} className="text-xs text-rose-400" role="alert">{error}</p>
                )}
            </div>
        );
    }
);

Textarea.displayName = 'Textarea';
